import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import ManualAssistant from "@/components/ManualAssistant";
import RTScheduleFab from "@/components/RTScheduleFab";
import PricingSimulatorDrawer from "@/components/PricingSimulatorDrawer";
import ClockInOutWidget from "@/components/ClockInOutWidget";
import TransferRequestsWidget from "@/components/TransferRequestsWidget";
import { ThemeToggle } from "@/components/ThemeToggle";
import TodaysFollowupsPopup from "@/components/TodaysFollowupsPopup";
import {
  LayoutDashboard, Users, FileText, Settings, LogOut, PlusCircle,
  Cable, Palette, Euro, Mail, User as UserIcon, SlidersHorizontal,
  Wrench, BarChart3, Headphones, Type, LayoutTemplate, Sparkles, Clock,
  BookOpen, ChevronDown, ChevronRight, Send, MapPin, Phone, Landmark, Building2,
  ShieldCheck, Trophy, AlertOctagon, Calculator, Tag, Bell,
  PanelLeftClose, PanelLeftOpen, ClipboardCheck, CalendarDays, Wallet, MessageSquare,
} from "lucide-react";
import { clearSession, getUser } from "@/lib/api";
import api from "@/lib/api";
import { canAccess } from "@/lib/permissions";
import { BrandMark } from "@/lib/branding";

// Primary navigation — visible to everyone with the right permission key.
const primaryNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", key: "dashboard" },
  { to: "/leads", label: "Leads", icon: Users, testid: "nav-leads", key: "leads" },
  { to: "/leads/new", label: "Nova Qualificação", icon: PlusCircle, testid: "nav-lead-new", key: "lead-new" },
  { to: "/leads/script", label: "Guião de Atendimento", icon: Headphones, testid: "nav-lead-script", key: "lead-new" },
  { to: "/followups", label: "Follow-ups", icon: Phone, testid: "nav-followups", key: "leads", badge: "followups" },
  { to: "/rts-pendentes", label: "RTs Pendentes", icon: ClipboardCheck, testid: "nav-rts-pendentes", key: "rts-pendentes" },
  { to: "/rts-calendar", label: "Calendário RTs", icon: CalendarDays, testid: "nav-rts-calendar", key: "rts-calendar" },
  { to: "/daf/reconcile", label: "Pagamentos (DAF)", icon: Landmark, testid: "nav-daf-reconcile", key: "finance-daf" },
  // 25/02/2026 — Cauções: acesso transversal. Badge com contagem de
  // leads em atraso (kickstart ou remanescente por liquidar) — funciona
  // como "sistema de alertas" para o utilizador saber quando entrar aqui.
  { to: "/deposits", label: "Cauções", icon: Wallet, testid: "nav-deposits", key: "deposits", badge: "deposits" },
  // 25/02/2026 — Painel simétrico do DAF: propostas por aceitar. Consultor
  // justifica por lead. Direcção usa para prever ganhos e detectar deals
  // parados. Partilha o mesmo routeKey `deposits` para simplicidade.
  { to: "/pending-proposals", label: "Propostas por aceitar", icon: MessageSquare, testid: "nav-pending-proposals", key: "deposits" },
  { to: "/margins", label: "Propostas Aceites · Margens", icon: Calculator, testid: "nav-margins", key: "margins" },
  { to: "/reports/daily", label: "Relatório Diário", icon: BarChart3, testid: "nav-daily-report", key: "daily-report" },
  { to: "/reports/offices", label: "Desempenho Escritórios", icon: Building2, testid: "nav-office-report", key: "office-report" },
  { to: "/care-plans", label: "Planos de Cuidados", icon: FileText, testid: "nav-care-plans", key: "care-plans" },
  { to: "/settings/offices", label: "Escritórios & Bónus", icon: Building2, testid: "nav-offices", key: "offices" },
  { to: "/bonuses", label: "Prémios (visão global)", icon: Trophy, testid: "nav-bonuses-overview", key: "bonuses-overview" },
  { to: "/performance", label: "Performance Atendimento", icon: Sparkles, testid: "nav-performance", key: "performance" },
  { to: "/alerts", label: "Alertas", icon: AlertOctagon, testid: "nav-alerts", key: "alerts" },
  { to: "/me/bonuses", label: "Os meus bónus", icon: Trophy, testid: "nav-my-bonuses", key: "my-bonuses" },
  { to: "/help", label: "Manual", icon: BookOpen, testid: "nav-manual", key: "help" },
];

// Admin nav — grouped into collapsible categories to keep the sidebar
// short. Each group starts collapsed by default; the "current path"
// auto-expands the group that contains the active route.
const adminGroups = [
  {
    id: "clientes",
    label: "Clientes",
    items: [
      { to: "/settings/pricing", label: "Tabela de Preços", icon: Euro, testid: "nav-pricing" },
      { to: "/settings/estimate-rules", label: "Regras da Estimativa", icon: MapPin, testid: "nav-estimate-rules" },
      { to: "/settings/fields", label: "Campos", icon: SlidersHorizontal, testid: "nav-fields" },
      { to: "/settings/qualification-tags", label: "Tags da Qualificação", icon: Tag, testid: "nav-qualification-tags" },
      { to: "/settings/rt-notes-snippets", label: "Textos rápidos RT", icon: Tag, testid: "nav-rt-snippets" },
      { to: "/settings/script", label: "Guião (config)", icon: Headphones, testid: "nav-script-settings" },
      { to: "/settings/public-copy", label: "Textos Páginas Cliente", icon: Type, testid: "nav-public-copy" },
      { to: "/settings/proposal-templates", label: "Layout Proposta", icon: LayoutTemplate, testid: "nav-proposal-templates" },
      { to: "/settings/ai-instructions", label: "Instruções à IA", icon: Sparkles, testid: "nav-ai-instructions" },
    ],
  },
  {
    id: "integracoes",
    label: "Integrações",
    items: [
      { to: "/settings/zoho", label: "Zoho CRM", icon: Cable, testid: "nav-zoho" },
      { to: "/settings/zoho/journey", label: "Zoho — Jornada", icon: Cable, testid: "nav-zoho-journey" },
      { to: "/settings/linkus", label: "Linkus (PBX)", icon: Cable, testid: "nav-linkus" },
      { to: "/linkus/discrepancies", label: "Divergências PBX", icon: Cable, testid: "nav-linkus-discrepancies" },
      { to: "/settings/clickup", label: "ClickUp", icon: Cable, testid: "nav-clickup" },
      { to: "/settings/email", label: "Office 365", icon: Mail, testid: "nav-email" },
    ],
  },
  {
    id: "organizacao",
    label: "Organização",
    items: [
      { to: "/settings/branding", label: "Marca (Logo)", icon: Palette, testid: "nav-branding" },
      { to: "/settings/users", label: "Utilizadores", icon: Settings, testid: "nav-users" },
      { to: "/settings/permissions", label: "Permissões", icon: ShieldCheck, testid: "nav-permissions" },
      { to: "/settings/attendance", label: "Ponto & GPS", icon: Clock, testid: "nav-attendance" },
      { to: "/settings/approvals", label: "Aprovadores Desconto", icon: ShieldCheck, testid: "nav-approvals" },
      { to: "/settings/proposal-prevalidation", label: "Validação de Propostas", icon: ShieldCheck, testid: "nav-proposal-prevalidation" },
      { to: "/settings/daily-report", label: "Relatório Diário", icon: Send, testid: "nav-daily-report" },
      { to: "/settings/baselines", label: "KPI Baselines", icon: BarChart3, testid: "nav-baselines" },
      { to: "/settings/maintenance", label: "Manutenção", icon: Wrench, testid: "nav-maintenance" },
      { to: "/settings/system", label: "Sistema (Go-Live)", icon: Wrench, testid: "nav-system" },
    ],
  },
];

// Sidebar states — Notion aesthetic: subtle background lift + text weight
// change for the active route, no left-edge coloured bar (Notion doesn't
// use those). Hover state = surface-hover token. Both variants adapt to
// dark mode via theme tokens.
const ACTIVE_BG =
  "bg-accent text-foreground font-medium border border-transparent";
const INACTIVE =
  "text-muted-foreground hover:text-foreground hover:bg-accent border border-transparent";

function AdminGroup({ group, currentPath }) {
  // Auto-expand the group that contains the current route.
  const containsCurrent = group.items.some(i => currentPath === i.to || currentPath.startsWith(i.to + "/"));
  const [open, setOpen] = useState(containsCurrent);
  useEffect(() => { if (containsCurrent) setOpen(true); }, [containsCurrent]);
  return (
    <div className="mb-1" data-testid={`nav-group-${group.id}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-2 py-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground hover:text-foreground transition-colors duration-150"
        data-testid={`nav-group-toggle-${group.id}`}
      >
        <span>{group.label}</span>
        {open ? <ChevronDown className="w-3 h-3" strokeWidth={1.6} /> : <ChevronRight className="w-3 h-3" strokeWidth={1.6} />}
      </button>
      {open && (
        <div className="mt-0.5 space-y-[1px]">
          {group.items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) => `flex items-center gap-2 px-2 py-1 rounded-[4px] text-[13px] transition-colors duration-150 ease-in-out ${isActive ? ACTIVE_BG : INACTIVE}`}
            >
              <n.icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.6} />
              <span className="truncate">{n.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Shell() {
  const nav_ = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(getUser());
  const [followupCount, setFollowupCount] = useState(0);
  // Aviso verde de envio · admin/direcao vê no topo o número de pedidos
  // de autorização de margem que estão pending há > 6h. Serve como
  // "próxima acção pendente" para minimizar tempo de reação.
  const [marginBadge, setMarginBadge] = useState({ count: 0, aged_count: 0 });
  const [simulatorOpen, setSimulatorOpen] = useState(false);
  const currentPath = location.pathname;

  // Auto-colapsar a sidebar quando o utilizador está na página de
  // detalhe de um lead (/leads/{uuid}) — deixa mais espaço horizontal
  // para a ficha do lead. Utilizador pode expandir manualmente com o
  // botão do topo. Regex exclui /leads, /leads/new e /leads/script.
  const LEAD_DETAIL_RE = /^\/leads\/(?!(new|script)$)[^/]+/;
  const isLeadDetail = LEAD_DETAIL_RE.test(currentPath);
  const [manualExpand, setManualExpand] = useState(false);
  useEffect(() => {
    // Sempre que o utilizador navega para fora do lead detail, reset
    // do override manual (para que o próximo lead volte a colapsar).
    if (!isLeadDetail) setManualExpand(false);
  }, [isLeadDetail]);
  const collapsed = isLeadDetail && !manualExpand;

  useEffect(() => {
    // 06/08/2026 — Preserve the deep-linked URL when kicking the user
    // to /login. Antes: consultor clicava em "Abrir e definir preço"
    // (email pontual) sem sessão → Shell fazia ``nav("/")`` e o
    // ``?focus=pontual-price`` do lead perdia-se. Guardamos aqui o
    // pathname + search para o ``Login`` re-navegar após auth.
    if (!user) {
      try {
        const target = window.location.pathname + window.location.search;
        // Só guardamos rotas internas plausíveis. Nunca redirect
        // para o próprio "/", nem loops infinitos.
        if (target && target !== "/" && !target.startsWith("/estimate/")
            && !target.startsWith("/proposal/") && !target.startsWith("/callback/")
            && !target.startsWith("/care-plan/") && !target.startsWith("/daf/")
            && !target.startsWith("/margin-authorization/")
            && !target.startsWith("/scenario/") && !target.startsWith("/justify/")) {
          window.sessionStorage.setItem("caring:pendingRedirect", target);
        }
      } catch (_) { /* storage unavailable */ }
      nav_("/");
    }
  }, [user, nav_]);

  // Refresh the pending follow-ups counter every 60s so the badge in
  // the sidebar stays up to date without spamming the API.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetch_ = async () => {
      try {
        const r = await api.get("/scheduled-followups/pending?scope=mine");
        if (!cancelled) setFollowupCount(r.data?.total || 0);
      } catch (_) { /* silent */ }
    };
    fetch_();
    const it = setInterval(fetch_, 60000);
    // Also refresh when the window regains focus.
    const onFocus = () => fetch_();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      clearInterval(it);
      window.removeEventListener("focus", onFocus);
    };
  }, [user]);

  // Aviso verde de envio — só admin/direcao. Refresh a cada 2 minutos,
  // e também quando a janela recupera foco. O backend retorna 0/0 para
  // outros roles, mas evita-se a chamada para poupar tráfego.
  useEffect(() => {
    if (!user || !["admin", "direcao"].includes(user.role)) return;
    let cancelled = false;
    const fetch_ = async () => {
      try {
        const r = await api.get("/margin-authorizations/badge?hours=6");
        if (!cancelled) setMarginBadge(r.data || { count: 0, aged_count: 0 });
      } catch (_) { /* silent */ }
    };
    fetch_();
    const it = setInterval(fetch_, 120000);
    const onFocus = () => fetch_();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      clearInterval(it);
      window.removeEventListener("focus", onFocus);
    };
  }, [user]);

  const logout = () => { clearSession(); nav_("/"); };
  if (!user) return null;

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <aside
        className={`hidden lg:flex flex-col ${collapsed ? "w-14" : "w-60"} border-r border-border bg-card/60 backdrop-blur-xl sticky top-0 h-screen transition-[width] duration-200`}
        data-testid="app-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
      >
        {/* Brand + collapse toggle + theme toggle */}
        <div className={`${collapsed ? "px-2" : "px-5"} py-4 border-b border-border flex items-center justify-between gap-2`}>
          {!collapsed && (
            <Link to="/dashboard" className="group inline-flex" data-testid="brand-link">
              <BrandMark size={32} />
            </Link>
          )}
          <div className="flex items-center gap-1.5">
            {!collapsed && <ThemeToggle />}
            {/* Aviso verde · pedidos de autorização de margem pending >6h.
                Só rendeeriza para admin/direcao quando aged_count > 0. */}
            {["admin", "direcao"].includes(user.role) && marginBadge.aged_count > 0 && (
              <Link
                to="/margin-authorizations"
                title={`${marginBadge.aged_count} pedido(s) sem decisão há mais de ${marginBadge.aged_threshold_hours || 6}h`}
                className="relative p-1.5 rounded-md text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 transition-colors animate-pulse"
                data-testid="margin-authz-bell"
              >
                <Bell className="w-4 h-4" strokeWidth={1.8} />
                <span
                  className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold flex items-center justify-center"
                  data-testid="margin-authz-bell-count"
                >
                  {marginBadge.aged_count > 9 ? "9+" : marginBadge.aged_count}
                </span>
              </Link>
            )}
            {isLeadDetail && (
              <button
                type="button"
                onClick={() => setManualExpand(v => !v)}
                data-testid="sidebar-toggle"
                className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                title={collapsed ? "Expandir menu" : "Colapsar menu"}
              >
                {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
              </button>
            )}
          </div>
        </div>

        {/* Nav — scrollable, tight spacing */}
        <nav className={`flex-1 ${collapsed ? "px-1.5" : "px-3"} py-3 overflow-y-auto space-y-0.5`}>
          {primaryNav.filter(n => canAccess(n.key, user)).map((n) => {
            // /leads NavLink needs `end` semantics so it isn't marked active on
            // /leads/new /leads/script (dedicated items). But it SHOULD stay
            // active on /leads/:id (lead detail) — same section from the user's
            // perspective. So we compute active manually.
            const isLeadsRoot = n.to === "/leads";
            const isActiveOverride = isLeadsRoot &&
              (currentPath === "/leads" || /^\/leads\/[a-zA-Z0-9-]+/.test(currentPath))
              && !currentPath.startsWith("/leads/new")
              && !currentPath.startsWith("/leads/script");
            return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to==='/leads' && !isActiveOverride}
              data-testid={n.testid}
              title={collapsed ? n.label : undefined}
              className={({ isActive }) => `flex items-center ${collapsed ? "justify-center px-1.5" : "gap-2.5 px-3"} py-1.5 rounded-md text-[13px] transition-colors ${(isActive || isActiveOverride) ? ACTIVE_BG : INACTIVE}`}
            >
              <n.icon className="w-3.5 h-3.5" strokeWidth={1.6} />
              {!collapsed && (
                <>
                  <span className="flex-1">{n.label}</span>
                  {n.badge === "followups" && followupCount > 0 && (
                    <span
                      data-testid="nav-followups-badge"
                      className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-[hsl(var(--danger))] text-white text-[10px] font-semibold"
                      title={`${followupCount} follow-up(s) pendente(s)`}
                    >
                      {followupCount > 99 ? "99+" : followupCount}
                    </span>
                  )}
                </>
              )}
            </NavLink>
            );
          })}

          {user.role === "admin" && !collapsed && (
            <div className="pt-4 mt-2 border-t border-border">
              {adminGroups.map(g => (
                <AdminGroup key={g.id} group={g} currentPath={currentPath} />
              ))}
            </div>
          )}
        </nav>

        {/* Profile + logout */}
        <div className={`${collapsed ? "px-1.5" : "px-3"} py-3 border-t border-border bg-card/40`}>
          {(user.role === "gestor" || user.role === "admin") && !collapsed && (
            <button
              onClick={() => setSimulatorOpen(true)}
              data-testid="btn-open-simulator"
              className="w-full inline-flex items-center gap-2 text-[12px] text-muted-foreground hover:text-primary hover:bg-accent transition-colors px-2 py-1.5 rounded-md mb-2 font-heading"
              title="Simulador de preços standalone (sem lead)"
            >
              <Calculator className="w-3.5 h-3.5" />
              <span className="truncate">Simulador de Preços</span>
            </button>
          )}
          {!collapsed && <ManualAssistant variant="sidebar" />}
          {!collapsed && <ClockInOutWidget user={user} />}
          <TransferRequestsWidget collapsed={collapsed} />
          <Link
            to="/profile"
            data-testid="nav-profile"
            className={`flex items-center ${collapsed ? "justify-center" : "gap-2.5"} mb-2 hover:bg-accent rounded-md p-1.5 -m-1.5 transition-colors`}
            title={collapsed ? user.name : undefined}
          >
            <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[11px] font-semibold font-heading">
              {user.name?.[0] || "?"}
            </div>
            {!collapsed && (
              <>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold truncate leading-tight text-foreground">{user.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground leading-tight mt-0.5 font-heading">{user.role}</div>
                </div>
                <UserIcon className="w-3 h-3 text-muted-foreground" />
              </>
            )}
          </Link>
          <button
            data-testid="logout-btn"
            onClick={logout}
            title={collapsed ? "Terminar sessão" : undefined}
            className={`w-full flex items-center ${collapsed ? "justify-center" : "gap-2"} text-[11px] text-muted-foreground hover:text-foreground transition-colors px-2 py-1.5 rounded-md hover:bg-accent`}
          >
            <LogOut className="w-3 h-3" />
            {!collapsed && <span>Terminar sessão</span>}
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
      <RTScheduleFab />
      <PricingSimulatorDrawer open={simulatorOpen} onClose={() => setSimulatorOpen(false)} />
      <TodaysFollowupsPopup />
    </div>
  );
}
